/**
 * The guided flow's stage registry — pure, and the only place a step's status is decided.
 *
 * NO STAGE STATE IS EVER STORED. Every status here is a function of the engine's own output
 * (`QoeAnalysis`), the saved `QoeInput`, and the engagement's sign-off and package record. A user
 * therefore cannot mark a step done; only the engine can, by producing an analysis that says so.
 * A parallel progress table would drift from `calculate()` and start lying to an owner about the
 * one number that matters.
 *
 * The plan's stage table is numbered 0-9, so there are ten steps here, from "What are we looking
 * at?" through "Publish it".
 */
import type { QoeAnalysis, QoeFinding, QoeInput } from '../../../../../data/qoeTypes'
import type { QoeHelpId } from '../qoeHelpContent'

/** The eight workpaper tabs, named once. `CfoQoePage` renders this list; the guide greys the ones a stage cannot reach yet. */
export type QoeWorkspaceTab = 'overview' | 'data' | 'earnings' | 'revenue' | 'cash' | 'transaction' | 'review' | 'deliverables'
export const QOE_WORKSPACE_TABS: ReadonlyArray<readonly [QoeWorkspaceTab, string]> = [
  ['overview', 'Overview'], ['data', 'Data & evidence'], ['earnings', 'Earnings'], ['revenue', 'Revenue'],
  ['cash', 'Cash'], ['transaction', 'NWC & debt'], ['review', 'Review'], ['deliverables', 'Deliverables'],
]

/**
 * The nine control layers the engine reconciles, named once for both surfaces: the workpapers print
 * `label` in the control-totals table, and the guided overview lists all nine with their status so a
 * practitioner can see the depth of the work in the first sixty seconds. `proves` is the owner-facing
 * one-liner for the same control.
 */
export const QOE_CONTROL_LAYERS: ReadonlyArray<{ id: string; label: string; proves: string }> = [
  { id: 'gl_to_tb_movement', label: 'GL to TB movement', proves: 'Every transaction adds up to the month it belongs in' },
  { id: 'tb_rollforward', label: 'TB roll-forward', proves: 'Each account opens where it closed last month' },
  { id: 'tb_period_continuity', label: 'TB period continuity', proves: 'No month is missing between the ends of the window' },
  { id: 'tb_to_financial_statement', label: 'TB to financial statements', proves: 'Your statements match the accounts underneath them' },
  { id: 'bank_rollforward', label: 'Bank roll-forward', proves: 'The bank statement moves the way the books say it did' },
  { id: 'sales_to_ledger_revenue', label: 'Sales to ledger revenue', proves: 'Your sales records and your ledger report the same revenue' },
  { id: 'ledger_equation', label: 'Ledger balances (debits = credits)', proves: 'The ledger itself balances' },
  { id: 'trial_balance_equation', label: 'Trial balance balances', proves: 'The trial balance itself balances' },
  { id: 'source_control_total', label: 'Source file control total', proves: 'Every imported file still holds the rows it arrived with' },
]

export type QoeStageId = 'scope' | 'books' | 'accounts' | 'tie_out' | 'adjustments' | 'revenue' | 'cash' | 'transaction' | 'review' | 'publish'
/** Derived on every render, never persisted. `blocked` means an engine error; `needs_attention` a warning. */
export type QoeStageStatus = 'blocked' | 'needs_attention' | 'done' | 'not_started'

/** The engagement facts a completion predicate needs that do not live on the analysis or the input. */
export interface QoeStageRecord {
  revision: number
  signoffs: ReadonlyArray<{ revision: number }>
  packages: ReadonlyArray<{ revision: number }>
}

export interface QoeStage {
  id: QoeStageId
  /** Owner-facing step title. The practitioner vocabulary stays on the workpaper tabs. */
  title: string
  /** One line, in the product's InsightExplain voice: why a buyer cares about this step. */
  why: string
  /** The workpaper tab this step maps to — the destination of "Open this in the workspace". */
  tab: QoeWorkspaceTab
  /** The verified help entry whose `what` / `how[]` / `limits[]` become this step's copy. */
  helpId: QoeHelpId
  /**
   * Set only for a step that renders no panel inline. The help entry's `how[]` operates a table, and
   * a reader who cannot see that table is being told to click things that are not on the screen —
   * so this replaces the numbered actions, and the deep link is how they reach the table.
   */
  withoutPanel?: string
  /** True only when the engine's own output says this step is finished. */
  isDone: (analysis: QoeAnalysis, input: QoeInput, record: QoeStageRecord) => boolean
  /** The exceptions this step owns. An error among them blocks the step; a warning asks for attention. */
  blockedBy: (findings: readonly QoeFinding[]) => QoeFinding[]
}

/**
 * Finding code -> step. The engine's seven workstreams already partition its finding codes almost
 * exactly along the steps, so this map carries only the overrides: codes whose workstream is not
 * where the owner does the work. `accounting_basis`, `earnings_period_gaps` and `earnings_duration`
 * are earnings/valuation codes about the WINDOW, which is set in step 0; `mapping_unreviewed` is an
 * earnings code about the CLASSIFICATIONS, which are reviewed in step 2.
 */
export const QOE_FINDING_STAGE: Readonly<Record<string, QoeStageId>> = {
  scope_missing: 'scope',
  accounting_basis: 'scope',
  earnings_period_gaps: 'scope',
  earnings_duration: 'scope',
  missing_accounting_source: 'books',
  source_limitation: 'books',
  mapping_unreviewed: 'accounts',
  eliminations_unreviewed: 'accounts',
  // A valuation code, but a statement about the earnings themselves rather than
  // about what a buyer deducts. An owner whose business did not earn anything in
  // the priced window acts on it in step 5, not under "what a buyer takes off".
  nonpositive_earnings: 'adjustments',
}

/** The fallback: a finding lands on the step that owns its workstream. */
export const QOE_WORKSTREAM_STAGE: Readonly<Record<string, QoeStageId>> = {
  coverage: 'books',
  source: 'books',
  reconciliation: 'tie_out',
  earnings: 'adjustments',
  revenue: 'revenue',
  cash: 'cash',
  nwc: 'transaction',
  debt: 'transaction',
  valuation: 'transaction',
}

/**
 * Returns null for a finding this client does not recognise. The guide renders those in their own
 * block rather than hiding them: a new engine code must never disappear from an owner's screen
 * because the client shipped before the map was updated.
 */
export const qoeStageForFinding = (finding: Pick<QoeFinding, 'code' | 'workstream'>): QoeStageId | null =>
  QOE_FINDING_STAGE[finding.code] || QOE_WORKSTREAM_STAGE[finding.workstream] || null

/** `info` findings describe the analysis rather than asking anything of a reader, so no step owns one. */
const exceptions = (findings: readonly QoeFinding[]) => findings.filter(f => f.severity !== 'info')
const owns = (id: QoeStageId) => (findings: readonly QoeFinding[]) => exceptions(findings).filter(f => qoeStageForFinding(f) === id)

export const QOE_STAGES: readonly QoeStage[] = [
  {
    id: 'scope',
    title: 'What are we looking at?',
    why: 'A buyer prices one specific set of books over one specific stretch of months. This is where you say which.',
    tab: 'overview',
    helpId: 'settings.form',
    // The engine raises scope_missing whenever the perimeter, the history window or the earnings
    // window is empty, so its absence is the complete test — there is nothing left to check.
    isDone: (analysis) => !analysis.findings.some(f => f.code === 'scope_missing'),
    blockedBy: owns('scope'),
  },
  {
    id: 'books',
    title: 'Bring in your books',
    why: 'Nothing below this line is real to a buyer until it comes out of your accounting system rather than a spreadsheet.',
    tab: 'data',
    helpId: 'data.capture.section',
    // Only the earnings window has to be supplied: the comparison months carry their own findings and
    // an owner is never asked to backfill history that no conclusion rests on.
    isDone: (analysis, input) => {
      const rows = analysis.coverage.filter(r => input.settings.earnings_periods.includes(r.period))
      return rows.length > 0 && rows.every(r => r.status === 'supplied')
    },
    blockedBy: owns('books'),
  },
  {
    id: 'accounts',
    title: 'Check what each account is',
    why: 'Whether an account counts as interest, depreciation or an everyday cost decides what your earnings even are.',
    tab: 'data',
    helpId: 'data.mapping.section',
    // The confirmation itself is the act, and the engine warns while it is unticked; both are required
    // so a stale analysis cannot report a review that the saved input does not carry.
    isDone: (analysis, input) => input.settings.account_mapping_reviewed === true && owns('accounts')(analysis.findings).length === 0,
    blockedBy: owns('accounts'),
  },
  {
    id: 'tie_out',
    title: 'Make the books tie out',
    why: "A buyer's accountant checks the same places against each other first. A difference here stops every other conversation.",
    tab: 'data',
    helpId: 'data.controls.section',
    // The controls table is one row per entity, month and account — thousands on a real engagement —
    // so this step renders the nine named layers on the depth card and deep-links to the table rather
    // than inlining it. Its help `how[]` instructs a reader to sort Difference rows and use the
    // Entity / month columns, none of which are on this screen, in words ("issuance",
    // "qualifications") the step never introduces.
    withoutPanel: 'These checks run by themselves every time your books come in, and the nine of them are listed further down this page. When one disagrees, the difference is named in the workspace with the entity, the month and the account it came from.',
    // A control with no counterpart is "unavailable", not reconciled: an unchecked control is never
    // evidence that it passed.
    isDone: (analysis) => analysis.reconciliations.length > 0 && analysis.reconciliations.every(r => r.status === 'reconciled'),
    blockedBy: owns('tie_out'),
  },
  {
    id: 'adjustments',
    title: 'Find the one-offs and owner items',
    why: "The things that happened once, or that stop at closing, are the difference between last year's profit and what a buyer is buying.",
    tab: 'earnings',
    helpId: 'earnings.ledger',
    // A clean company can finish this step with no adjustments at all, so the test is that earnings
    // calculate and nothing is outstanding — never that some number of add-backs exists.
    //
    // A proposal nobody has dispositioned raises no finding, so the engine has no opinion about it
    // and the derived status would report none: the step read "Done" with a real add-back sitting in
    // it, unreviewed. This is the one place the "only the engine can mark a step done" architecture
    // needs help, and `input.adjustments` is already the engine's own input, not a parallel record.
    isDone: (analysis, input) => analysis.earnings.complete === true
      && owns('adjustments')(analysis.findings).length === 0
      && !input.adjustments.some(a => a.status === 'proposed'),
    blockedBy: owns('adjustments'),
  },
  {
    id: 'revenue',
    title: 'Show your revenue holds up',
    why: 'A buyer pays more for revenue that comes back next year than for revenue that had to be won once.',
    tab: 'revenue',
    helpId: 'revenue.sales_schedule',
    // Accounting revenue totals alone say nothing about retention or concentration; the analysis has
    // groups only once a sales schedule with customer, location and service detail is in.
    isDone: (analysis) => analysis.revenue.length > 0 && owns('revenue')(analysis.findings).length === 0,
    blockedBy: owns('revenue'),
  },
  {
    id: 'cash',
    title: 'Prove the cash is real',
    why: 'Earnings are an opinion until the bank statement agrees with them.',
    tab: 'cash',
    helpId: 'cash.proof',
    isDone: (analysis) => analysis.cash.length > 0 && owns('cash')(analysis.findings).length === 0,
    blockedBy: owns('cash'),
  },
  {
    id: 'transaction',
    title: 'What a buyer takes off the price',
    why: 'Cash, debt and the working capital you hand over at closing move the cheque as much as the earnings do.',
    tab: 'transaction',
    helpId: 'transaction.equity_bridge_card',
    // A chosen working capital target is the substance of this step; without one the closing true-up
    // has nothing to measure against.
    isDone: (analysis) => analysis.nwc.selected_peg != null && owns('transaction')(analysis.findings).length === 0,
    blockedBy: owns('transaction'),
  },
  {
    id: 'review',
    title: 'Have someone else check it',
    why: 'The person who prepared a version cannot sign it off, and a buyer will ask who did.',
    tab: 'review',
    // The sign-off card, not the review desk: the desk's copy is about clearing findings and what
    // becomes a qualification, and says nothing about a second person — the one thing this step's
    // title promises and its completion predicate tests. Adjudicating individual adjustments is
    // step 5's work, and step 5's panel already deep-links to the desk.
    helpId: 'review.signoff_card',
    // Bound to the CURRENT revision: any saved change creates a new draft, and last week's review has
    // not seen it.
    isDone: (_analysis, _input, record) => record.signoffs.some(s => s.revision === record.revision),
    blockedBy: owns('review'),
  },
  {
    id: 'publish',
    title: 'Publish it',
    why: 'Publishing freezes exactly what you showed, so a month from now nobody has to argue about which version they read.',
    tab: 'deliverables',
    helpId: 'deliverables.issue',
    isDone: (_analysis, _input, record) => record.packages.some(p => p.revision === record.revision),
    blockedBy: owns('publish'),
  },
]

export const QOE_STAGE_BY_ID: Readonly<Record<QoeStageId, QoeStage>> =
  Object.fromEntries(QOE_STAGES.map(stage => [stage.id, stage])) as Record<QoeStageId, QoeStage>

/** Owner-facing status words. "Blocked" is the only practitioner-shaped one, and it is the plainest word for it. */
export const QOE_STATUS_LABEL: Readonly<Record<QoeStageStatus, string>> = {
  blocked: 'Blocked',
  needs_attention: 'Needs you',
  done: 'Done',
  not_started: 'Not started',
}

export interface QoeStep {
  stage: QoeStage
  status: QoeStageStatus
  /** Every exception this step owns, engine order preserved. */
  findings: QoeFinding[]
  errors: number
  warnings: number
  /** False when an earlier step is blocked: the numbers downstream of an error are not yet worth reading. */
  reachable: boolean
}

export interface QoeGuideState {
  steps: QoeStep[]
  /** Findings whose code and workstream this client does not map. Rendered, never dropped. */
  unmapped: QoeFinding[]
  done: number
  total: number
  /** The first step that is not done — the single thing to do next. */
  next: QoeStep | null
}

/** The engagement shape `deriveSteps` needs. Narrower than `QoeDetail` so the tests can build one by hand. */
export interface QoeStageSource {
  engagement: { revision: number }
  input: QoeInput
  analysis: QoeAnalysis
  signoffs?: ReadonlyArray<{ revision: number }> | null
  packages?: ReadonlyArray<{ revision: number }> | null
}

/**
 * Derives every step's status from the engagement. Called on each render — there is nowhere to cache
 * it that would not be able to disagree with the analysis on screen.
 */
export function deriveSteps(detail: QoeStageSource): QoeGuideState {
  const { analysis, input } = detail
  const record: QoeStageRecord = {
    revision: detail.engagement.revision,
    signoffs: detail.signoffs || [],
    packages: detail.packages || [],
  }
  const findings = analysis.findings || []
  const unmapped = exceptions(findings.filter(f => qoeStageForFinding(f) === null))
  /**
   * An exception this build cannot place could belong to ANY step — that is what "cannot place"
   * means — so while an ERROR among them exists, no step reads as finished and nothing
   * downstream is worth reading. Without this the hero renders "10 of 10 steps done. Every step is
   * done for this version." beside an engine conclusion that the package cannot be issued: the
   * client's map would be the only thing between the engine and a false "finished", and the engine
   * grows finding codes faster than this file does. Unreachable with today's seven workstreams, and
   * that is the point — it is the fail-safe for the eighth.
   */
  const unplaceable = unmapped.some(f => f.severity === 'error')
  let blockedEarlier = unplaceable
  const steps = QOE_STAGES.map(stage => {
    const owned = stage.blockedBy(findings)
    const errors = owned.filter(f => f.severity === 'error').length
    const warnings = owned.length - errors
    // The plan's order: an error blocks, then a warning asks, then the completion predicate decides
    // between done and not started. A step with no findings that is not finished has simply not begun.
    const status: QoeStageStatus = errors > 0
      ? 'blocked'
      : warnings > 0
        ? 'needs_attention'
        : !unplaceable && stage.isDone(analysis, input, record) ? 'done' : 'not_started'
    const reachable = !blockedEarlier
    if (status === 'blocked') blockedEarlier = true
    return { stage, status, findings: owned, errors, warnings, reachable }
  })
  return {
    steps,
    unmapped,
    done: steps.filter(s => s.status === 'done').length,
    total: steps.length,
    next: steps.find(s => s.status !== 'done') || null,
  }
}

/** The tabs a guided reader can open right now. A tab is reachable when no earlier step is blocked. */
export function reachableTabs(state: QoeGuideState): Set<QoeWorkspaceTab> {
  const open = new Set<QoeWorkspaceTab>()
  for (const step of state.steps) if (step.reachable) open.add(step.stage.tab)
  return open
}
